import React, { useState } from 'react';
import { authAPI } from '../services/api';
import './Register.css';

const Register = ({ onRegisterSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    registrationCode: '',
    agreeToTOS: true, // Default to checked
    agreeToRules: true, // Default to checked
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username must be no more than 30 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
    } else if (!formData.username[0].match(/[a-zA-Z0-9]/)) {
      newErrors.username = 'Username must start with a letter or number';
    } else if (!formData.username[formData.username.length - 1].match(/[a-zA-Z0-9]/)) {
      newErrors.username = 'Username must end with a letter or number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.registrationCode.trim()) {
      newErrors.registrationCode = 'Registration code is required';
    }

    if (!formData.agreeToTOS) {
      newErrors.agreeToTOS = 'You must agree to the Terms of Service';
    }

    if (!formData.agreeToRules) {
      newErrors.agreeToRules = 'You must agree to the Community Rules';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await authAPI.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        registration_code: formData.registrationCode,
      });

      // Store the token and send to post-registration page
      localStorage.setItem('token', response.data.access_token);
      setShowSuccess(true);
      window.location.href = '/registration-complete';
      
      // Call the success callback if provided
      if (onRegisterSuccess) {
        onRegisterSuccess(response.data);
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.detail || 'Registration failed';
        setErrors({ general: errorMessage });
      } else {
        setErrors({ general: 'Network error. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="register-success">
        <h2>Registration Successful!</h2>
        <p>Thank you for registering with SortaFriends!</p>
        <p>Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-logo">
        <img src="/assets/registration.png" alt="SortaFriends Registration" className="logo-image" />
      </div>
      <h2>Create Your Account</h2>
      <p className="register-subtitle">
        Join SortaFriends today!
      </p>

      <form onSubmit={handleSubmit} className="register-form">
        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={errors.username ? 'error' : ''}
            placeholder="Choose a username"
          />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
            placeholder="Enter your email"
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={errors.password ? 'error' : ''}
            placeholder="Create a password"
          />
          {errors.password && <span className="error-text">{errors.password}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={errors.confirmPassword ? 'error' : ''}
            placeholder="Confirm your password"
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="registrationCode">Registration Code</label>
          <input
            type="text"
            id="registrationCode"
            name="registrationCode"
            value={formData.registrationCode}
            onChange={handleChange}
            className={errors.registrationCode ? 'error' : ''}
            placeholder="Enter your registration code"
          />
          {errors.registrationCode && <span className="error-text">{errors.registrationCode}</span>}
          <small className="help-text">
            Enter the registration code you received.
          </small>
        </div>

        <div className="form-group">
          <label style={{ 
            display: 'block',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: '1.4',
            marginBottom: '8px'
          }}>
            <input
              type="checkbox"
              id="agreeToTOS"
              name="agreeToTOS"
              checked={formData.agreeToTOS}
              onChange={(e) => setFormData(prev => ({ ...prev, agreeToTOS: e.target.checked }))}
            />
            I agree to the{' '}
            <a 
              href="/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
            >
              Terms of Service
            </a>
          </label>
          {errors.agreeToTOS && <span className="error-text">{errors.agreeToTOS}</span>}
        </div>

        <div className="form-group">
          <label style={{ 
            display: 'block',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: '1.4',
            marginBottom: '8px'
          }}>
            <input
              type="checkbox"
              id="agreeToRules"
              name="agreeToRules"
              checked={formData.agreeToRules}
              onChange={(e) => setFormData(prev => ({ ...prev, agreeToRules: e.target.checked }))}
            />
            I agree to the{' '}
            <a 
              href="/rules" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
            >
              Community Rules
            </a>
          </label>
          {errors.agreeToRules && <span className="error-text">{errors.agreeToRules}</span>}
        </div>

        <button 
          type="submit" 
          className="register-button"
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>

      <div className="register-footer">
        <p>Already have an account? <a href="/login">Sign in here</a></p>
      </div>
    </div>
  );
};

export default Register; 